import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, QueryList, ViewChildren } from "@angular/core";
import { Router } from "@angular/router";
import { List } from "immutable";
import { timer } from "rxjs";
import { switchMap, takeWhile } from "rxjs/operators";
import { SubSink } from "subsink";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { ListItem } from "../../../../shared/list/list-item";
import { IOnUploadNew, UploadNewComponent } from "../../../../shared/upload/upload-new.component";
import { StringHelper } from "../../../../utilities/contracts/string-helper";
import { BulkProjectValidationStatus, IBulkProjectRow, IBulkProjectValidationResult } from "./bulk-project-response.model";
import { BulkProjectUploadService } from "./bulk-project-upload.service";

const BULK_PROJECT_FILE_TYPE = "BulkProject";
const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const VALIDATION_POLL_INTERVAL_MS = 2000;

@Component({
  selector: "app-bulk-project-upload",
  templateUrl: "./bulk-project-upload.component.html",
  styleUrls: ["./bulk-project-upload.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkProjectUploadComponent implements OnDestroy {

  fileName: string;
  fileLocation: string;
  loadId: string;
  isFileUploaded = false;
  isValidated = false;
  isProjectValidationPassed = false;
  totalPassed = 0;
  totalFailed = 0;
  isProcessingVisible = false;
  processingHeaderText: string;
  totalRequested = 0;
  validationRows: IBulkProjectRow[] = [];
  summaryReport = List<ListItem>();
  private isPollingComplete = false;
  private readonly subscriptions = new SubSink();
  @ViewChildren("appFileUpload") filesToUpload: QueryList<UploadNewComponent>;

  constructor(
    private bulkProjectUploadService: BulkProjectUploadService,
    private messagingService: MessagingService,
    private changeDetector: ChangeDetectorRef,
    private router: Router
  ) { }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get summaryHeader(): string {
    return "VALIDATION SUMMARY";
  }

  get validationStatusMessage(): string {
    if (this.totalFailed === 0) {
      return "PASSED";
    }

    if (this.totalPassed === 0) {
      return "FAILED";
    }

    return "PARTIALLY FAILED";
  }

  get hasFailures(): boolean {
    return this.totalFailed > 0;
  }

  get canCreateProjects(): boolean {
    return this.totalPassed > 0;
  }

  trackByIndex(index) {
    return index;
  }

  uploadDocument(event: IOnUploadNew): void {
    const file = event.formData.getAll("Document")[0] as File;

    if (!file) {
      return;
    }

    this.processingHeaderText = "PLEASE WAIT...UPLOAD IS IN PROGRESS";
    this.isProcessingVisible = true;
    this.changeDetector.markForCheck();

    const payload = {
      name: file.name,
      type: file.type || XLSX_MIME_TYPE,
      fileType: BULK_PROJECT_FILE_TYPE,
    };

    this.bulkProjectUploadService.getPresignedURL(JSON.stringify(payload)).subscribe({
      next: response => {
        if (response && response.presignedURL) {
          this.bulkProjectUploadService.uploadFile(response.presignedURL, file)
            .then(() => {
              this.fileName = file.name;
              this.fileLocation = response.fileName;
              this.isFileUploaded = true;
              this.isProcessingVisible = false;
              this.messagingService.showToast("File uploaded successfully.", SeverityType.SUCCESS);
              this.changeDetector.markForCheck();
            })
            .catch(() => {
              this.onUploadError();
            });
        } else {
          this.onUploadError();
        }
      },
      error: () => {
        this.onUploadError();
      },
    });
  }

  validate(): void {
    if (!this.isFileUploaded) {
      this.messagingService.showToast("Please upload an .xlsx file.", SeverityType.ERROR);
      return;
    }

    this.isValidated = false;
    this.isPollingComplete = false;
    this.processingHeaderText = "PLEASE WAIT.. VALIDATION IS IN PROGRESS";
    this.isProcessingVisible = true;

    this.subscriptions.sink = this.bulkProjectUploadService.startValidation(this.fileLocation).subscribe(
      result => {
        this.loadId = result.loadId;
        this.handleValidationResult(result);

        if (!this.isPollingComplete) {
          this.pollValidationStatus();
        }
      },
      () => this.onValidationError());
  }

  createProjects(): void {
    if (!this.loadId || !this.canCreateProjects) {
      return;
    }

    this.isPollingComplete = false;
    this.processingHeaderText = "PLEASE WAIT.. PROJECT CREATION IS IN PROGRESS";
    this.isProcessingVisible = true;

    this.subscriptions.sink = this.bulkProjectUploadService.startCreation(this.loadId).subscribe(
      result => {
        this.handleValidationResult(result);

        if (!this.isPollingComplete) {
          this.pollValidationStatus();
        }
      },
      () => this.onValidationError());
  }

  reset(): void {
    this.filesToUpload.forEach(fileToUpload => {
      fileToUpload.clearUpload();
    });
    this.fileName = null;
    this.fileLocation = null;
    this.loadId = null;
    this.isFileUploaded = false;
    this.isValidated = false;
    this.isPollingComplete = true;
    this.isProjectValidationPassed = false;
    this.totalPassed = 0;
    this.totalFailed = 0;
    this.validationRows = [];
    this.summaryReport = List<ListItem>();
    this.changeDetector.markForCheck();
  }

  downloadValidationIssues(): void {
    // Only the failed rows are exported, with the columns defined for the sheet's project type
    // (RISK / IVA / HEDIS), plus a trailing "Failed Reason" column.
    const failedRows = this.validationRows.filter(row => !row.isValid);
    if (failedRows.length === 0) {
      return;
    }

    const columns = this.getIssueColumns(failedRows[0].projectType);
    const header = columns.map(column => column.header);
    const csvRows = failedRows.map((row, index) =>
      columns.map(column =>
        column.field === "rowNumber"
          ? `${index + 1}`
          : StringHelper.escapeCsvValue(row[column.field] as string)
      ).join(","));
    const csv = [header.join(","), ...csvRows].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Validation-Issues.csv";
    a.click();
    a.remove();
  }

  private getIssueColumns(projectType: string): { header: string; field: keyof IBulkProjectRow }[] {
    const type = (projectType || "").toUpperCase();

    const columns: { header: string; field: keyof IBulkProjectRow }[] = [
      { header: "Row Number", field: "rowNumber" },
      { header: "Project Name", field: "projectName" },
      { header: "Project Type", field: "projectType" },
    ];

    if (type === "HEDIS") {
      columns.push(
        { header: "Project Start Date", field: "projectStartDate" },
        { header: "Project End Date", field: "projectEndDate" },
        { header: "Measurement Year", field: "measurementYear" },
        { header: "Reporting Year", field: "reportingYear" }
      );
    } else if (type === "IVA") {
      columns.push(
        { header: "Review Period From Date", field: "reviewPeriodFromDate" },
        { header: "Review Period To Date", field: "reviewPeriodToDate" },
        { header: "Project Start Date", field: "projectStartDate" },
        { header: "Project End Date", field: "projectEndDate" },
        { header: "LOB", field: "lob" },
        { header: "Coding Model", field: "codingModel" }
      );
    } else {
      columns.push(
        { header: "Review Period From Date", field: "reviewPeriodFromDate" },
        { header: "Review Period To Date", field: "reviewPeriodToDate" },
        { header: "Project Start Date", field: "projectStartDate" },
        { header: "Project End Date", field: "projectEndDate" },
        { header: "Risk Program Type", field: "riskProgramType" },
        { header: "Coding Review Mode", field: "codingReviewMode" },
        { header: "LOB", field: "lob" },
        { header: "Coding Model", field: "codingModel" }
      );
    }

    columns.push(
      { header: "Product Type", field: "productType" },
      { header: "Group", field: "group" },
      { header: "Copy Provider Packet", field: "copyProviderPacket" },
      { header: "Source Project Name", field: "sourceProjectName" },
      { header: "Failed Reason", field: "failedReason" }
    );

    return columns;
  }

  getValidationMessageClass(value: string): string {
    switch (value) {
      case "FAILED":
        return "summary-body__details--danger";
      case "PASSED":
        return "summary-body__details--pass";
      default:
        return "summary-body__details";
    }
  }

  private pollValidationStatus(): void {
    this.subscriptions.sink = timer(VALIDATION_POLL_INTERVAL_MS, VALIDATION_POLL_INTERVAL_MS)
      .pipe(
        takeWhile(() => !this.isPollingComplete),
        switchMap(() => this.bulkProjectUploadService.getValidationStatus(this.loadId))
      )
      .subscribe(
        result => this.handleValidationResult(result),
        () => this.onValidationError());
  }

  private handleValidationResult(result: IBulkProjectValidationResult): void {
    if (!result) {
      return;
    }

    const status = (result.status || "").toUpperCase();

    if (status === BulkProjectValidationStatus.Failed.toUpperCase()) {
      this.isPollingComplete = true;
      this.isProcessingVisible = false;
      this.messagingService.showToast(result.errorMessage || "Validation failed. Please try again.", SeverityType.ERROR);
      this.changeDetector.markForCheck();
      return;
    }

    if (status === BulkProjectValidationStatus.Completed.toUpperCase()) {
      this.isPollingComplete = true;
      this.isProcessingVisible = false;
      this.bulkProjectUploadService.creationResult.next(result);
      this.router.navigate(["/project/bulkupload/success"]);
      this.changeDetector.markForCheck();
      return;
    }

    if (status === BulkProjectValidationStatus.ValidationComplete.toUpperCase()) {
      this.isPollingComplete = true;
      this.totalRequested = result.totalRequested;
      this.totalPassed = result.totalPassed;
      this.totalFailed = result.totalFailed;
      this.isProjectValidationPassed = result.totalFailed === 0;
      this.validationRows = result.rows || [];

      this.summaryReport = List<ListItem>();
      this.summaryReport = this.summaryReport.push(
        new ListItem({ key: "Total Projects", value: result.totalRequested.toString(), url: "" }),
        new ListItem({ key: "Total Projects Passed", value: result.totalPassed.toString(), url: "" }),
        new ListItem({ key: "Total Projects Failed", value: result.totalFailed.toString(), url: "" }),
        new ListItem({ key: "Validation Status", value: this.validationStatusMessage, url: "" })
      );

      this.isValidated = true;
      this.isProcessingVisible = false;
      this.changeDetector.markForCheck();
    }
  }

  private onValidationError(): void {
    this.isPollingComplete = true;
    this.isProcessingVisible = false;
    this.messagingService.showToast("Please try again", SeverityType.ERROR);
    this.changeDetector.markForCheck();
  }

  private onUploadError(): void {
    this.isProcessingVisible = false;
    this.messagingService.showToast("Error uploading file using pre-signed URL, please try again.", SeverityType.ERROR);
    this.reset();
  }
}
